<template>
    <div class="uk-margin-medium-top uk-clearfix">
        <div v-show="options.length == 0">
            {{ $t('Es wurde keine Zahlungsmethode in den Projekteinstellungen definiert.') }}
        </div>
        <div v-show="!loginusername && options.length > 0 && waitingForCountry">
            <h2>{{ $t("Zahlungsart auswählen") }}</h2>
            <p>{{ $t('Die Zahlungsarten erscheinen, sobald ein Land ausgewählt ist.') }}</p>
        </div>
        <div v-show="!loginusername && options.length > 0 && !waitingForCountry && shopOptions.length == 0">
            {{ $t('Für das gewählte Land ist keine Zahlungsart verfügbar.') }}
        </div>

        <!-- Reseller-POS: Tender-Auswahl als zwei große Tap-Targets -->
        <div v-if="loginusername && options.length > 0">
            <h2>{{ $t("Zahlungsart") }}</h2>
            <div class="uk-grid-small uk-child-width-1-2@s reseller-tender-block" :class="{invalid: error}" uk-grid>
                <div v-for="opt in options" :key="opt.tender">
                    <label
                        class="reseller-tender-card"
                        :class="{
                            'reseller-tender-card--active': tender === opt.tender,
                            'reseller-tender-card--disabled': opt.disabled,
                        }"
                    >
                        <input
                            class="reseller-tender-radio"
                            type="radio"
                            v-model="tender"
                            :value="opt.tender"
                            :disabled="opt.disabled"
                            name="reseller-tender"
                        />
                        <div class="reseller-tender-icon">{{ opt.icon }}</div>
                        <div class="reseller-tender-label">{{ $t(opt.label) }}</div>
                        <div v-if="opt.disabled" class="uk-text-meta uk-margin-small-top">
                            {{ $t(opt.disabledHint) }}
                        </div>
                    </label>
                </div>
            </div>

            <!-- Terminal-Picker nur bei card_terminal mit >1 Terminals -->
            <div v-if="tender === 'card_terminal' && terminals.length > 1"
                 class="uk-margin-medium-top">
                <h3>{{ $t("Terminal auswählen") }}</h3>
                <select v-model="terminalId" class="uk-select" style="max-width: 400px">
                    <option v-for="t in terminals" :key="t.id" :value="t.id">{{ t.label }}</option>
                </select>
            </div>
        </div>

        <!-- Normalshop: unverändert -->
        <div v-show="!loginusername && shopOptions.length > 1">
            <h2>{{ $t("Zahlungsart auswählen") }}</h2>
            <div class="uk-form-controls input-section uk-width-1-1 uk-width-1-2@s uk-width-1-3@m uk-float-left paymentTypeBlock" :class="{invalid: error}">
                <label
                    v-for="paymentOption in shopOptions"
                    :key="paymentOption.type"
                    class="payment-option uk-display-block uk-margin-small-top uk-card uk-card uk-card-default uk-width-1-1 uk-padding-small"
                >
                    <input
                        class="uk-radio uk-margin-small-right"
                        type="radio"
                        v-model="paymentType"
                        :value="paymentOption.type"
                        name="payment"
                    />
                    <img v-if="paymentOption.icon" :src="'data:image/svg+xml,'+paymentOption.icon" class="payment-icon" style="height:20px" />
                    <img v-if="paymentOption.img" :src="paymentOption.img" style="width: 100%; max-width: 280px" />

                    {{ $t(paymentOption.label) }}
                </label>
            </div>
        </div>
        <div class="uk-float-left additionalsBlock" :class="{'uk-margin-large-left' : options.length > 1, 'uk-margin-small-top' : options.length > 1}">
            <div v-if="settings.coupons_enabled && !settings.coupons_first" class="uk-margin-small-bottom uk-margin-small-top">
                <label for="couponSwitch" style="user-select:none">
                    <input id="couponSwitch" v-model="couponSwitch" @change="formatDonation($event)" class="uk-checkbox" type="checkbox">
                    {{ $t('Ich habe einen Gutschein-Code') }}
                </label>
                <div class="uk-form-controls uk-margin-small-top" v-if="couponSwitch">
                    <input id="couponCode" v-model="couponCode" class="uk-input" type="text" :placeholder="$t('Gutschein-Code')">
                    <p class="uk-text-small uk-margin-remove-top">({{ $t('Die Prüfung des Gutscheins erfolgt im nächsten Schritt') }})</p>
                </div>
            </div>
            <div class="uk-margin-small-top" v-if="settings.newsletter_signup_enabled && !loginusername">
                <label for="newsletterSignup" style="user-select:none">
                    <input id="newsletterSignup" v-model="newsletterSignup" class="uk-checkbox" type="checkbox">
                    {{ settings.newsletter_signup_text ? settings.newsletter_signup_text : $t('Ich möchte auf dem Laufenden bleiben') }}
                </label>
            </div>
        </div>
        <div
            v-if="settings.donations_enabled"
            class="donation-card-wrapper uk-margin-medium-top uk-width-1-1 uk-width-1-2@m">
            <div class="donation-card uk-card uk-card-body uk-card-small uk-text-center">
                <h4 class="uk-margin-remove-top uk-margin-small-bottom">
                    <span uk-icon="icon: heart; ratio: 1.2" class="donation-card__icon uk-margin-small-right"></span>
                    {{ $t('Spende') }}
                </h4>
                <p class="uk-margin-small uk-text-muted">
                    {{ settings.donation_text ? settings.donation_text : $t('Wir freuen uns über eine freiwillige Spende.') }}
                </p>
                <div class="uk-flex uk-flex-center uk-flex-middle uk-flex-wrap uk-grid-small" uk-grid>
                    <div v-for="amount in suggestedAmounts" :key="amount">
                        <button
                            type="button"
                            class="uk-button uk-border-pill"
                            :class="suggestedActive(amount) ? 'uk-button-primary' : 'uk-button-default'"
                            @click="toggleDonationAmount(amount)">
                            {{ amount }}€
                        </button>
                    </div>
                </div>
                <div class="uk-margin-small-top">
                    <input
                        v-model="customAmountInput"
                        class="uk-input uk-width-1-1 uk-text-center uk-border-pill"
                        type="number"
                        min="1"
                        step="1"
                        :placeholder="$t('Anderer Betrag')"
                        id="donationAmount">
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import paymentTypes from "../../forms/paymentTypes.json";
import useVuelidate from "@vuelidate/core";
import {required} from "@vuelidate/validators";
import helper from "../../helper/helper";
import {allowedPaymentTypes} from "../../helper/paymentCountryRules";

/**
 * Reseller-Tender catalog. Maps each backend-allowed tender to the
 * payment_type the API expects, plus the label/icon shown on the POS.
 *
 * @gotcha The DB column `payment_type` distinguishes only "directsale"
 *         (cash, no PSP) from "mollie" (any Mollie product, here POS).
 *         The Reseller-only `tender` discriminator is what flips
 *         TransactionLifecycle to openAndFinishBar vs. openUnbar+TapToPay
 *         vs. markExternalProvider (cashless).
 * @gotcha `cashless` shares the `directsale` payment_type with `cash` but
 *         must NOT be sent as `cash`: the guest pays at the cashless
 *         provider's terminal, which fiscalises that revenue. A cash tender
 *         would sign the same sale a second time on our own TSE.
 */
const RESELLER_TENDERS = {
    cash: {
        tender: 'cash',
        paymentType: 'directsale',
        icon: '💶',
        label: 'Bar kassieren',
    },
    cashless: {
        tender: 'cashless',
        paymentType: 'directsale',
        icon: '🪙',
        label: 'Cashless kassieren',
    },
    card_terminal: {
        tender: 'card_terminal',
        paymentType: 'mollie',
        icon: '💳',
        label: 'Mit Karte kassieren',
    },
};

export default {
    mixins: [helper],
    props: ['settings', 'loginusername'],
    components: {},
    data: () => {
        return {
            v$: useVuelidate(),
            options: [],
            terminals: [],
        }
    },
    validations() {
        return this.loginusername
            ? { tender: { required } }
            : { paymentType: { required } };
    },
    computed: {
        error() {
            return this.loginusername ? this.v$.tender?.$error : this.v$.paymentType.$error;
        },
        allowedTypes() {
            return allowedPaymentTypes(this.store.state.paymentCountryRules, this.store.state.billingCountry);
        },
        /** Normal-shop tiles after the country rules; the reseller POS keeps `options`. */
        shopOptions() {
            const allowed = this.allowedTypes;
            return allowed === null ? this.options : this.options.filter(option => allowed.includes(option.type));
        },
        waitingForCountry() {
            return Array.isArray(this.allowedTypes) && this.allowedTypes.length === 0;
        },
        paymentType: {
            get() { return this.store.state.paymentType; },
            set(newValue) { this.store.state.paymentType = newValue; },
        },
        tender: {
            get() { return this.store.state.tender; },
            set(newValue) {
                this.store.state.tender = newValue;
                const def = RESELLER_TENDERS[newValue];
                if (def) {
                    this.store.state.paymentType = def.paymentType;
                }
                if (newValue === 'card_terminal' && this.terminals.length === 1) {
                    this.store.state.terminalId = this.terminals[0].id;
                }
                if (newValue !== 'card_terminal') {
                    this.store.state.terminalId = '';
                }
            },
        },
        terminalId: {
            get() { return this.store.state.terminalId; },
            set(newValue) { this.store.state.terminalId = newValue; },
        },
        couponSwitch: {
            get() { return this.store.state.couponSwitch; },
            set(newValue) { this.store.state.couponSwitch = newValue; },
        },
        couponCode: {
            get() { return this.store.state.couponCode; },
            set(newValue) { this.store.state.couponCode = newValue; },
        },
        donationSwitch: {
            get() { return this.store.state.donationSwitch; },
            set(newValue) { this.store.state.donationSwitch = newValue; },
        },
        donationAmount: {
            get() { return this.store.state.donationAmount; },
            set(newValue) { this.store.state.donationAmount = newValue; },
        },
        newsletterSignup: {
            get() { return this.store.state.newsletterSignup; },
            set(newValue) { this.store.state.newsletterSignup = newValue; },
        },
        suggestedAmounts() {
            // Configured per project (php-core setting DonationSuggestedAmounts); older
            // backends don't send the key, so the former fixed amounts stay the fallback.
            const configured = (this.settings.donation_amounts || [])
                .map(amount => parseInt(amount))
                .filter(amount => amount > 0);
            return configured.length ? configured : [2, 5, 10];
        },
        customAmountInput: {
            get() {
                const stored = parseInt(this.donationAmount);
                if (!this.donationSwitch || !stored || this.suggestedAmounts.includes(stored)) {
                    return '';
                }
                return String(stored);
            },
            set(newValue) {
                const n = parseInt(newValue);
                if (n > 0) {
                    this.donationSwitch = true;
                    this.donationAmount = n + '€';
                } else {
                    this.donationSwitch = false;
                    this.donationAmount = '0€';
                }
            },
        },
    },
    mounted() {
        if (this.loginusername) {
            this.buildResellerOptions();
        } else {
            Object.keys(this.settings.paymentOptions).forEach(paymentOption => {
                if (paymentTypes[paymentOption]) {
                    const option = paymentTypes[paymentOption];
                    if (!option.hide || this.settings.paymentOptions) {
                        option.type = paymentOption;
                        if (this.settings.paymentOptions[paymentOption].custom_image) {
                            option.img = this.settings.paymentOptions[paymentOption].custom_image
                        }
                        this.options.push(option);
                    }
                } else {
                    this.emitError('payment', `Bezahlmethode <strong class="uk-text-uppercase">${paymentOption}</strong> noch nicht implementiert.`, true)
                }
            })
            this.syncPaymentType();
        }
    },
    watch: {
        shopOptions() {
            if (!this.loginusername) {
                this.syncPaymentType();
            }
        },
    },
    methods: {
        /**
         * Reseller-Backend liefert paymentOptions in der Form
         *   { cash: [], card_terminal: { terminals: [...] } }
         * Tender, die im Backend gefiltert werden (kein TSS, keine Terminals
         * konfiguriert), kommen erst gar nicht an. Hier nur noch Render-Logik.
         */
        buildResellerOptions() {
            const opts = this.settings.paymentOptions || {};
            Object.keys(RESELLER_TENDERS).forEach(key => {
                if (!(key in opts)) return;
                const def = { ...RESELLER_TENDERS[key] };
                if (key === 'card_terminal') {
                    // Backend liefert Terminals als normalisierte Liste
                    //   [{ id: 'term_abc123', label: 'Kasse 1' }, …]
                    // (siehe TicketShopController::normalizeMollieTerminals).
                    // Fallbacks decken legacy-Shapes ab, falls ein älteres Backend
                    // noch das rohe Object schickt.
                    const terminals = (opts.card_terminal && opts.card_terminal.terminals) || [];
                    if (Array.isArray(terminals)) {
                        this.terminals = terminals.map(t => {
                            if (t && typeof t === 'object') {
                                return { id: String(t.id || ''), label: String(t.label || t.id || '') };
                            }
                            return { id: String(t), label: String(t) };
                        }).filter(t => t.id);
                    } else if (terminals && typeof terminals === 'object') {
                        this.terminals = Object.entries(terminals).map(([k, v]) => {
                            const ks = String(k);
                            const vs = String(v || '');
                            // Legacy: {term_xxx: "label"} — key=id
                            if (ks.startsWith('term_') && !vs.startsWith('term_')) {
                                return { id: ks, label: vs || ks };
                            }
                            // Current: {label: term_xxx} — value=id
                            return { id: vs || ks, label: ks };
                        });
                    } else {
                        this.terminals = [];
                    }
                    if (this.terminals.length === 0) {
                        def.disabled = true;
                        def.disabledHint = 'Kein Mollie-Terminal konfiguriert.';
                    }
                }
                this.options.push(def);
            });

            if (this.options.length === 1 && !this.options[0].disabled) {
                this.tender = this.options[0].tender;
            }
        },
        /**
         * Picks the only tile automatically and drops a choice the country no
         * longer allows (buyer changed the country after choosing).
         */
        syncPaymentType() {
            if (this.shopOptions.length === 1) {
                this.paymentType = this.shopOptions[0].type;
            } else if (this.paymentType && !this.shopOptions.some(option => option.type === this.paymentType)) {
                this.paymentType = '';
            }
        },
        validate() {
            this.v$.$touch();
            if (this.v$.$invalid) return false;
            if (this.loginusername && this.tender === 'card_terminal' && !this.terminalId) {
                this.emitError('payment', 'Bitte ein Terminal auswählen.', false);
                return false;
            }
            return true;
        },

        suggestedActive(amount) {
            return this.donationSwitch
                && this.suggestedAmounts.includes(parseInt(this.donationAmount))
                && parseInt(this.donationAmount) === amount;
        },
        toggleDonationAmount(amount) {
            if (this.suggestedActive(amount)) {
                this.donationSwitch = false;
                this.donationAmount = '0€';
                return;
            }
            this.donationSwitch = true;
            this.donationAmount = amount + '€';
        },

        formatDonation(e) {
            if ($('#donationAmount').length) {
                let donationField = $('#donationAmount')[0]
                let currentPosition = donationField.selectionDirection=='backward' ? donationField.selectionStart : donationField.selectionEnd
                let currentValue = parseInt($('#donationAmount').val().replace(/[^0-9]/, ''))
                if (isNaN(currentValue)) {
                    currentValue = 0
                }
                this.donationAmount = currentValue+'€'
                $('#donationAmount').val(this.donationAmount)
                if (e.key && e.key.length == 1) {
                    let carretPosition = Math.max(0, Math.min(currentPosition, $('#donationAmount').val().length-1))
                    if ("0123456789".indexOf(e.key) == -1) {
                        carretPosition = Math.max(0, carretPosition - 1)
                    }
                    donationField.setSelectionRange(carretPosition, carretPosition)
                }
            }
        }
    }
}
</script>

<style lang="scss">
.payment-option {
    padding-left: 40px !important;
    padding-right: 11px !important;
    input {
        position: absolute;
        top: calc(50% - 4px);
        left: 17px;
    }
    .payment-icon {
        height: 20px;
        margin-left: -6px;
        margin-right: -8px;
        margin-top: -4px;
    }
}
// Reseller-Tender-Card-Styles wohnen jetzt in
// plugins/.../assets/styles/ticketshop/_reseller.scss — der Theme-Combiner
// lädt die mit, der Vue-extrahierte ticketshop.css wird vom Theme nicht
// gelinkt.
</style>
